/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  MenuItem,
  Typography,
} from '@mui/material';
import { isEqual } from 'lodash';
import React from 'react';
import type { FieldErrors, FieldValues } from 'react-hook-form';
import {
  ReactHookFormHiddenInput,
  ReactHookFormSelect,
  ReactHookFormTextField,
  isValidGeoJsonCoordinates,
} from '@opengeoweb/form-fields';
import { AlertBanner, CustomIconButton } from '@opengeoweb/shared';
import type { DrawMode } from '@opengeoweb/webmap-react';
import {
  getIcon,
  defaultLineString,
  defaultSmoothLine,
  defaultOval,
  defaultLocation,
  defaultRotatableBox,
  defaultCircle,
  getDrawModeIdFromSelectionType,
  defaultSquare,
  defaultPoint,
  defaultPolygon,
  defaultFreehandLine,
  defaultDelete,
} from '@opengeoweb/webmap-react';
import { useWarningsTranslation } from '../../utils/i18n';

export const opacityOptions = [100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0];

const drawToolConfig = [
  { primaryMode: defaultPolygon },
  {
    primaryMode: defaultLineString,
    secondaryMode: defaultSmoothLine,
  },
  { primaryMode: defaultFreehandLine },
  { primaryMode: defaultCircle, secondaryMode: defaultOval },
  { primaryMode: defaultSquare, secondaryMode: defaultRotatableBox },
  { primaryMode: defaultPoint, secondaryMode: defaultLocation },
  { primaryMode: defaultDelete },
];

/**
 * Utility to determine if the secondary draw mode button should be shown
 * @param mode DrawMode of the primary button
 * @param secondaryMode DrawMode of the secondary button
 * @param activeDrawModeId Currently active DrawMode id
 * @param geoJSONDrawMode DrawMode id derived from the GeoJSON selection type
 * @returns
 */
const shouldDisplayButton = (
  mode: DrawMode,
  secondaryMode: DrawMode,
  activeDrawModeId: string,
  geoJSONDrawMode?: string,
): boolean => {
  if (
    activeDrawModeId === mode.drawModeId ||
    activeDrawModeId === secondaryMode.drawModeId ||
    geoJSONDrawMode === secondaryMode.drawModeId
  ) {
    return true;
  }

  return false;
};

export interface DrawingToolFormValues {
  objectName: string;
  geoJSON: GeoJSON.FeatureCollection;
  id: string;
}

export interface DrawingToolFormContentsProps {
  isLoading?: boolean;
  dialogError?: string;
  id?: string;
  drawModes: DrawMode[];
  activeDrawModeId: string;
  opacity: number;
  isInEditMode: boolean;
  isDirty: boolean;
  errors: FieldErrors<FieldValues>;
  onChangeDrawMode: (newMode: DrawMode) => void;
  onChangeOpacity: (event: SelectChangeEvent<unknown>) => void;
  onBlurName: (event: React.FocusEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  geoJSON?: GeoJSON.FeatureCollection;
}

export const DrawingToolFormContentsNoMemo: React.FC<
  DrawingToolFormContentsProps
> = ({
  isLoading,
  dialogError,
  id,
  drawModes,
  onChangeDrawMode,
  activeDrawModeId,
  isInEditMode,
  isDirty,
  errors,
  onChangeOpacity,
  onBlurName,
  opacity,
  onSubmit,
  geoJSON,
}) => {
  const { t } = useWarningsTranslation();
  const hasErrors = Object.keys(errors).length > 0;

  // Only show the draw modes that are available in the current context
  const filteredDrawModes = drawToolConfig.filter((configItem) =>
    drawModes.find(
      (mode) => mode.drawModeId === configItem.primaryMode.drawModeId,
    ),
  );

  const geoJSONDrawMode = getDrawModeIdFromSelectionType(
    geoJSON?.features?.[0]?.properties?.selectionType,
    drawModes,
  );

  return (
    <>
      <Box
        sx={{
          height: 'calc( 100% - 72px )',
          overflow: 'auto',
        }}
      >
        {!!dialogError && (
          <Box
            sx={{
              width: '100%',
            }}
          >
            <AlertBanner shouldClose title={dialogError} />
          </Box>
        )}
        <Grid
          container
          data-testid="drawingToolForm"
          spacing={1}
          sx={{
            padding: 2,
            flexDirection: 'column',
          }}
        >
          <Grid>
            <Typography variant="body2">
              {t('drawing-tool-form-tools')}
            </Typography>
          </Grid>
          <Grid
            container
            sx={{
              marginBottom: 1,
            }}
          >
            {filteredDrawModes.map(({ primaryMode, secondaryMode }) => {
              const showSecondaryButton =
                secondaryMode &&
                drawModes.find(
                  (mode) => mode.drawModeId === secondaryMode.drawModeId,
                ) &&
                shouldDisplayButton(
                  primaryMode,
                  secondaryMode,
                  activeDrawModeId,
                  geoJSONDrawMode,
                );
              return (
                <Box
                  key={primaryMode.drawModeId}
                  sx={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                  }}
                >
                  <CustomIconButton
                    data-testid={primaryMode.drawModeId}
                    disabled={isLoading}
                    isSelected={
                      !!primaryMode.isSelectable &&
                      activeDrawModeId === primaryMode.drawModeId
                    }
                    onClick={(): void => onChangeDrawMode(primaryMode)}
                    shouldShowExtraOptionsIndicator={!!secondaryMode}
                    size="medium"
                    tooltipTitle={t(`${primaryMode.title}`)}
                    variant="tool"
                  >
                    {getIcon(primaryMode.selectionType)}
                  </CustomIconButton>
                  {showSecondaryButton ? (
                    <CustomIconButton
                      data-testid={secondaryMode.drawModeId}
                      disabled={isLoading}
                      isSelected={
                        !!secondaryMode.isSelectable &&
                        activeDrawModeId === secondaryMode.drawModeId
                      }
                      onClick={(): void => onChangeDrawMode(secondaryMode)}
                      size="medium"
                      sx={{ marginTop: 1 }}
                      tooltipTitle={t(`${secondaryMode.title}`)}
                      variant="tool"
                    >
                      {getIcon(secondaryMode.selectionType)}
                    </CustomIconButton>
                  ) : null}
                </Box>
              );
            })}
            {!!isInEditMode && (
              <Typography
                component="span"
                sx={{
                  display: 'inline-block',
                  marginLeft: 2,
                  top: -1,
                  position: 'relative',
                  fontSize: 14,
                }}
              >
                {t('drawing-tool-form-exit-draw-mode')}
              </Typography>
            )}
            {!!errors.geoJSON && !!isDirty && (
              <FormHelperText error sx={{ marginLeft: 0 }} variant="filled">
                {errors.geoJSON.message as string}
              </FormHelperText>
            )}
            <ReactHookFormHiddenInput
              name="geoJSON"
              rules={{
                validate: {
                  coordinatesNotEmpty: (value): boolean | string =>
                    isValidGeoJsonCoordinates(value!) ||
                    t('drawing-tool-form-drawing-required'),
                },
              }}
            />
          </Grid>
          <Grid container size="grow">
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <ReactHookFormSelect
                autoFocus
                data-testid="drawingToolForm-opacity"
                disabled={isLoading}
                isReadOnly={false}
                label={t('drawing-tool-form-opacity')}
                name="opacity"
                onChange={onChangeOpacity}
                rules={{}}
                value={opacity * 100}
              >
                {opacityOptions.map((opacity) => (
                  <MenuItem key={opacity} value={opacity}>
                    {opacity} %
                  </MenuItem>
                ))}
              </ReactHookFormSelect>
            </Grid>
          </Grid>
          <Grid>
            <ReactHookFormTextField
              disabled={isLoading}
              isReadOnly={false}
              label={t('drawing-tool-form-object-name')}
              name="objectName"
              onBlur={onBlurName}
              onKeyDown={(event) => event.stopPropagation()}
              rules={{ required: true }}
            />
            <ReactHookFormHiddenInput name="id" />
          </Grid>
        </Grid>
      </Box>
      <Box
        sx={{
          padding: '24px 10px 14px 10px',
          position: 'absolute',
          bottom: 0,
          width: '100%',
          backgroundColor: 'geowebColors.background.surfaceApp',
          boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.5)',
          zIndex: 1,
        }}
      >
        <Button
          data-testid="saveDrawingFormButton"
          disabled={isLoading || hasErrors}
          onClick={onSubmit}
          sx={{ width: '100%', textTransform: 'none', fontSize: '12px' }}
          variant="primary"
        >
          {id !== ''
            ? t('drawing-tool-form-update')
            : t('drawing-tool-form-save')}
        </Button>
      </Box>
    </>
  );
};

export const DrawingToolFormContents = React.memo(
  DrawingToolFormContentsNoMemo,
  isEqual,
);
